/**
 * 뼈대 로딩 (v2.317.0 · Salesforce 식 stencil).
 *
 *   - 「불러오는 중…」 글자 대신 자료가 들어올 자리 모양의 회색 막대를 보여 준다.
 *   - 0.3초 안에 끝나는 로딩은 아무것도 안 그린다(깜빡임 방지 · SLDS loading 지침 300ms).
 *   - 표 자리에는 <LoadingRows />(줄 여러 개 · 아래로 갈수록 옅어짐), 글 자리에는 <LoadingLines />.
 *   - 색은 slate-200 → slate-50 로 흐르는 띠. 움직임 줄이기(prefers-reduced-motion)면 멈춘 막대.
 */
import { useEffect, useState } from 'react';

function useDelayed(ms = 300): boolean {
  const [show, setShow] = useState(ms === 0);
  useEffect(() => {
    if (ms === 0) return;
    const t = setTimeout(() => setShow(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return show;
}

const BAR = 'h-3 rounded-sm bg-slate-200 motion-safe:animate-pulse';

/** 글 몇 줄 자리 — 가운데 정렬된 칸(td/div) 안에서도 폭을 잡게 inline-block */
export function LoadingLines({ lines = 3, delayMs = 300, className = '' }: { lines?: number; delayMs?: number; className?: string }) {
  const show = useDelayed(delayMs);
  if (!show) return <div className={`min-h-[2rem] ${className}`} aria-busy="true" />;
  const widths = ['w-11/12', 'w-9/12', 'w-10/12', 'w-7/12', 'w-8/12'];
  return (
    <div className={`inline-block w-full max-w-md text-left ${className}`} aria-busy="true" aria-label="불러오는 중">
      <div className="flex flex-col gap-2 py-1">
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className={`${BAR} ${widths[i % widths.length]}`} style={{ opacity: 1 - i * 0.12 }} />
        ))}
      </div>
    </div>
  );
}

/** 표 자리 — 줄마다 칸 몇 개. 아래로 갈수록 옅어진다(SLDS stencil 지침). */
export function LoadingRows({ rows = 6, cols = 5, delayMs = 300, className = '' }: { rows?: number; cols?: number; delayMs?: number; className?: string }) {
  const show = useDelayed(delayMs);
  if (!show) return <div className={`min-h-[6rem] ${className}`} aria-busy="true" />;
  return (
    <div className={`flex flex-col gap-2.5 p-3 ${className}`} aria-busy="true" aria-label="불러오는 중">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3" style={{ opacity: Math.max(0.25, 1 - r * 0.12) }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className={`${BAR} ${c === 0 ? 'w-24' : c === cols - 1 ? 'w-20' : 'flex-1'}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** 카드 하나 자리 */
export function LoadingCard({ className = '' }: { className?: string }) {
  const show = useDelayed(300);
  if (!show) return <div className={`min-h-[6rem] ${className}`} aria-busy="true" />;
  return (
    <div className={`bg-white rounded border border-slate-200 p-4 ${className}`} aria-busy="true" aria-label="불러오는 중">
      <div className={`${BAR} w-1/3 mb-3`} />
      <LoadingLines lines={3} delayMs={0} />
    </div>
  );
}
